import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Rocket, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { COMPANY_SETUP_PATHS } from "@/routes/company/routes.company-setup";
import {
  useDismissCompanySetupMutation,
  useGetCompanySetupProgressQuery,
} from "@/services/(company)/api.company-setup";
import { useSetupCopy } from "../useSetupCopy";

/**
 * Dashboard nudge toward the setup guide, shown until the workspace is live or
 * the banner is dismissed.
 *
 * This is the guide's discovery path: the sidebar is built server-side from
 * `platform_sections`, so until a row exists for the guide it has no sidebar
 * entry — this banner and global search are how an admin finds it.
 *
 * Renders nothing rather than a skeleton while loading: a placeholder at the
 * top of the dashboard would push the whole canvas down on every visit, and a
 * banner is not what the user came to the page for.
 */
export const SetupGuideBanner = () => {
  const { t } = useTranslation("company");
  const { stepTitle } = useSetupCopy();
  const { data, isLoading } = useGetCompanySetupProgressQuery();
  const [dismiss, { isLoading: isDismissing }] =
    useDismissCompanySetupMutation();

  const progress = data?.data;

  if (isLoading || !progress) return null;
  if (progress.isReadyToGoLive || progress.dismissedAt) return null;

  const { completionPercentage, completedRequiredSteps, totalRequiredSteps, nextStep } =
    progress;

  return (
    // rounded-xs matches the two real callout precedents in the app: the
    // Alert primitive and BranchReadinessNotice (payroll readiness banner).
    <section className="relative rounded-xs border border-primary/30 bg-primary-muted p-4">
      <PermissionButton
        section={PLATFORM_SECTION_ENUM.COMPANY_SETUP}
        action="settings"
        mode="hide"
        variant="ghost"
        size="icon-xs"
        className="absolute end-2 top-2"
        aria-label={t("company:company_setup.dismiss")}
        isLoading={isDismissing}
        onClick={() => dismiss()}
      >
        <X className="size-3.5" />
      </PermissionButton>

      <div className="flex flex-col gap-3 pe-8 sm:flex-row sm:items-center sm:gap-4">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Rocket className="size-4" />
        </span>

        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground">
            {t("company:company_setup.banner_title")}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {nextStep
              ? t("company:company_setup.banner_progress_next", {
                  done: completedRequiredSteps,
                  total: totalRequiredSteps,
                  next: stepTitle(nextStep),
                })
              : t("company:company_setup.banner_progress", {
                  done: completedRequiredSteps,
                  total: totalRequiredSteps,
                })}
          </p>
          <Progress value={completionPercentage} className="mt-2 h-1.5" />
        </div>

        <Button asChild size="sm" variant="primary" className="shrink-0">
          <Link to={COMPANY_SETUP_PATHS.ROOT}>
            {t("company:company_setup.continue_setup")}
            <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      </div>
    </section>
  );
};
